// Client-side auth: session retrieval, mock-mode helpers, login/logout.
//
// AUTH_MODE:
//   mock  — local dev without Entra. A picked email/roles pair is stored in
//           localStorage and sent as X-Mock-User-* headers by the api client.
//   entra — Microsoft Entra SSO via the API BFF. The browser holds no tokens;
//           the API sets an httpOnly session cookie.
//
// Until services/api exists (Milestone 3), loadAuthConfig() fails and the app
// falls back to mock mode. The mock session lives entirely in the browser, so
// the back-office runs standalone against local mock data.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError } from "./api";

export type AuthMode = "mock" | "entra";
export type AppEnv = "local" | "acceptance" | "production";

// Roles offered by the mock login — match the API's RBAC roles (Prisma UserRole).
export const MOCK_ROLES = [
  "admin",
  "planner",
  "calculation",
  "customer_service",
  "qhse",
  "field",
] as const;

const fallbackMode: AuthMode =
  (import.meta.env.VITE_AUTH_MODE as AuthMode | undefined) ??
  (import.meta.env.DEV ? "mock" : "entra");

let resolvedMode: AuthMode | null = null;
let resolvedAppEnv: AppEnv | null = null;

export async function loadAuthConfig(): Promise<AuthMode> {
  try {
    // Cap the wait so a slow/unreachable API can't blank the first paint. The
    // app falls back to the build-time default (mock in dev) if this times out.
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("auth config timeout")), 3000),
    );
    const cfg = await Promise.race([
      apiClient.get<{ mode: AuthMode; appEnv?: AppEnv }>("/api/v1/auth/config"),
      timeout,
    ]);
    resolvedMode = cfg.mode === "mock" || cfg.mode === "entra" ? cfg.mode : fallbackMode;
    resolvedAppEnv = cfg.appEnv ?? null;
  } catch {
    resolvedMode = fallbackMode;
  }
  return resolvedMode;
}

export function authMode(): AuthMode {
  return resolvedMode ?? fallbackMode;
}

export function appEnv(): AppEnv {
  return (
    resolvedAppEnv ??
    (import.meta.env.VITE_APP_ENV as AppEnv | undefined) ??
    "local"
  );
}

// The principal returned by GET /api/v1/auth/me.
export interface SessionPrincipal {
  userId: string;
  email: string;
  displayName?: string;
  roles: string[];
  userType: "internal";
  isMock: boolean;
}

// ── Mock session (dev only) ──────────────────────────────────────────────────
const MOCK_KEY = "avds-mock-session";

export interface MockSession {
  email: string;
  roles: string[];
}

export function getMockSession(): MockSession | null {
  if (authMode() !== "mock") return null;
  try {
    const raw = localStorage.getItem(MOCK_KEY);
    return raw ? (JSON.parse(raw) as MockSession) : null;
  } catch {
    return null;
  }
}

export function setMockSession(session: MockSession): void {
  localStorage.setItem(MOCK_KEY, JSON.stringify(session));
}

export function clearMockSession(): void {
  localStorage.removeItem(MOCK_KEY);
}

// ── Session query ────────────────────────────────────────────────────────────
export const sessionQueryKey = ["session"] as const;

function displayNameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase() + p.slice(1))
    .join(" ");
}

export async function fetchSession(): Promise<SessionPrincipal | null> {
  // Mock mode: resolve the session locally from the stored dev session. No
  // stored session means "not logged in". (Once the API exists, entra mode and
  // mock mode both go through GET /api/v1/auth/me — see the entra branch below.)
  if (authMode() === "mock") {
    const mock = getMockSession();
    if (!mock) return null;
    return {
      userId: "mock-user",
      email: mock.email,
      displayName: displayNameFromEmail(mock.email),
      roles: mock.roles,
      userType: "internal",
      isMock: true,
    };
  }
  try {
    return await apiClient.get<SessionPrincipal>("/api/v1/auth/me");
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      return null;
    }
    throw err;
  }
}

export function useSession() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: fetchSession,
    staleTime: 5 * 60_000,
  });
}

export function hasAnyRole(
  session: SessionPrincipal | null | undefined,
  roles: string[],
): boolean {
  return !!session?.roles.some((r) => roles.includes(r));
}

// ── Login / logout ───────────────────────────────────────────────────────────
export function login(redirect?: string): void {
  // entra: hand off to the API BFF, which redirects to Entra and back — to
  // `redirect` when given (the API only accepts a same-origin path).
  window.location.href = redirect
    ? `/api/v1/auth/login?redirect=${encodeURIComponent(redirect)}`
    : "/api/v1/auth/login";
}

export function useLogout() {
  const queryClient = useQueryClient();
  return async () => {
    if (authMode() === "mock") {
      clearMockSession();
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      return;
    }
    try {
      await apiClient.post("/api/v1/auth/logout", {});
    } finally {
      window.location.href = "/login";
    }
  };
}
